import { useEffect, useState } from "react";
import { Plus, Globe, Archive, PencilLine } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { EmptyState } from "../../components/shared/EmptyState";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { MediaPickerDialog } from "../../components/shared/MediaPickerDialog";
import { api, formatApiError } from "../../lib/api";

const STATUS_LABELS = { brouillon: "Brouillon", publie: "Publié", archive: "Archivé" };
const EMPTY = { title: "", place: "", date: "", excerpt: "", description: "", tags: "", images: "", featured: false, sections: [], review_text: "", review_author: "", review_source: "", review_rating: 5, vendors: [], product_slugs: "" };

export default function GalleryAdminPage() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = () => api.get("/admin/gallery").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (g) => {
    setEditing(g);
    setForm({
      title: g.title, place: g.place || "", date: g.date || "", excerpt: g.excerpt || "", description: g.description || "",
      tags: (g.tags || []).join(", "), images: (g.images || []).join("\n"), featured: g.featured || false,
      sections: g.sections || [], review_text: g.review_text || "", review_author: g.review_author || "",
      review_source: g.review_source || "", review_rating: g.review_rating || 5,
      vendors: g.vendors || [], product_slugs: (g.product_slugs || []).join(", "),
    });
    setOpen(true);
  };

  const submit = async () => {
    const payload = {
      ...form,
      images: form.images.split("\n").map((s) => s.trim()).filter(Boolean), cover_image: null,
      date: form.date || null, place: form.place || null, excerpt: form.excerpt || null, description: form.description || null,
      tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
      sections: form.sections.filter((s) => s.title || s.text || s.image),
      vendors: form.vendors.filter((v) => v.name),
      review_text: form.review_text || null, review_author: form.review_author || null,
      review_source: form.review_source || null, review_rating: form.review_text ? Number(form.review_rating) : null,
      product_slugs: form.product_slugs.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) { await api.patch(`/admin/gallery/${editing.id}`, payload); toast.success("Réalisation mise à jour"); }
      else { await api.post("/admin/gallery", payload); toast.success("Réalisation créée (brouillon)"); }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setStatus = async (g, status) => {
    try { await api.post(`/admin/gallery/${g.id}/status`, { status }); toast.success(status === "publie" ? "Publié — visible sur le site" : status === "archive" ? "Archivé" : "Repassé en brouillon"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!items) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="gallery-loading">Chargement…</div>;

  return (
    <div data-testid="admin-gallery-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-ae">B18 — Réalisations &amp; Galerie</p>
          <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Réalisations &amp; Galerie</h1>
          <p className="mt-1 text-sm text-muted-foreground">Publication volontaire uniquement — alimente les pages publiques Réalisations.</p>
        </div>
        <button type="button" onClick={openCreate} className="btn-primary-ae !px-5 !py-2.5 !text-xs" data-testid="gallery-create">
          <Plus className="h-4 w-4" /> Nouvelle réalisation
        </button>
      </div>

      {items.length === 0 ? (
        <div className="mt-6"><EmptyState title="Aucune réalisation">Les vraies réalisations seront créées ici par Art Event.</EmptyState></div>
      ) : (
        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="gallery-grid">
          {items.map((g) => (
            <div key={g.id} className="card-ae overflow-hidden" data-testid={`gallery-card-${g.id}`}>
              {g.images?.[0] && <img src={g.cover_image || g.images[0]} alt={g.title} className="aspect-[16/9] w-full object-cover" />}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-heading text-base text-deepgreen">{g.title} {g.featured && <span className="text-gold">★</span>}</p>
                    <p className="text-xs text-muted-foreground">{g.place || "—"}{g.date ? ` — ${g.date}` : ""}</p>
                  </div>
                  <StatusBadge status={STATUS_LABELS[g.status]} />
                </div>
                <div className="mt-3 flex gap-2">
                  <button type="button" onClick={() => openEdit(g)} className="rounded-full border border-border px-3 py-1.5 text-[11px] font-bold text-deepgreen hover:bg-sage-light" data-testid={`gallery-edit-${g.id}`}><PencilLine className="h-3.5 w-3.5" /></button>
                  {g.status !== "publie" && <button type="button" onClick={() => setStatus(g, "publie")} className="rounded-full bg-deepgreen px-3 py-1.5 text-[11px] font-bold text-white" data-testid={`gallery-publish-${g.id}`}><Globe className="mr-1 inline h-3.5 w-3.5" />Publier</button>}
                  {g.status === "publie" && <button type="button" onClick={() => setStatus(g, "archive")} className="rounded-full border border-border px-3 py-1.5 text-[11px] font-bold text-deepgreen hover:bg-sage-light" data-testid={`gallery-archive-${g.id}`}><Archive className="mr-1 inline h-3.5 w-3.5" />Archiver</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-cream max-w-2xl" data-testid="gallery-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-deepgreen">{editing ? "Modifier la réalisation" : "Nouvelle réalisation"}</DialogTitle>
            <DialogDescription>Photos depuis la Médiathèque centrale (remplaçables à tout moment).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2"><label className="label-ae" htmlFor="g-title">Titre</label><input id="g-title" className="input-ae" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="gallery-title" /></div>
              <div><label className="label-ae" htmlFor="g-place">Lieu</label><input id="g-place" className="input-ae" value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} data-testid="gallery-place" /></div>
              <div><label className="label-ae" htmlFor="g-date">Date</label><input id="g-date" type="date" className="input-ae" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="gallery-date" /></div>
              <div className="sm:col-span-2"><label className="label-ae" htmlFor="g-excerpt">Résumé</label><input id="g-excerpt" className="input-ae" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="gallery-excerpt" /></div>
              <div className="sm:col-span-2"><label className="label-ae" htmlFor="g-desc">Description</label><textarea id="g-desc" className="input-ae min-h-24" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="gallery-description" /></div>
              <div className="sm:col-span-2">
                <label className="label-ae" htmlFor="g-images">Photos (une URL par ligne — ordre conservé)</label>
                <textarea id="g-images" className="input-ae min-h-20 font-mono text-xs" value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} data-testid="gallery-images" />
                <button type="button" onClick={() => setPickerOpen(true)} className="btn-secondary-ae mt-2 !px-4 !py-1.5 !text-xs" data-testid="gallery-pick-media">Ajouter depuis la Médiathèque</button>
                {form.images.trim() && <img src={form.images.trim().split("\n")[0]} alt="Aperçu de la première photo" className="mt-2 h-16 w-auto rounded-lg border border-border object-cover" data-testid="gallery-image-preview" />}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm font-semibold text-deepgreen">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-4 w-4 accent-[#2C4C3B]" data-testid="gallery-featured" />
              Mise en avant sur l'accueil
            </label>

            {/* Badges SEO (thème, prestations…) */}
            <div>
              <label className="label-ae" htmlFor="g-tags">Badges SEO — thème &amp; prestations (séparés par des virgules)</label>
              <input id="g-tags" className="input-ae" placeholder="ex : Champêtre, Arche cérémonie, Décoration florale" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="gallery-tags" />
            </div>

            {/* Storytelling : sections alternées texte/photo */}
            <div className="rounded-lg border border-sage/40 bg-white p-4" data-testid="gallery-sections-editor">
              <div className="flex items-center justify-between">
                <p className="label-ae !mb-0">Récit immersif — sections alternées texte/photo</p>
                <button type="button" onClick={() => setForm({ ...form, sections: [...form.sections, { title: "", text: "", image: "" }] })} className="btn-secondary-ae !px-3.5 !py-1.5 !text-[10px]" data-testid="gallery-add-section">
                  <Plus className="h-3.5 w-3.5" /> Ajouter une section
                </button>
              </div>
              {form.sections.map((s, i) => (
                <div key={i} className="mt-3 space-y-2 rounded-lg bg-cream p-3" data-testid={`gallery-section-${i}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-deepgreen">Section {i + 1}</p>
                    <button type="button" onClick={() => setForm({ ...form, sections: form.sections.filter((_, j) => j !== i) })} className="text-[11px] font-bold text-red-600" data-testid={`gallery-section-remove-${i}`}>Retirer</button>
                  </div>
                  <input className="input-ae !py-2 !text-xs" placeholder="Titre de la section" value={s.title} onChange={(e) => setForm({ ...form, sections: form.sections.map((x, j) => j === i ? { ...x, title: e.target.value } : x) })} data-testid={`gallery-section-title-${i}`} />
                  <textarea className="input-ae min-h-16 !py-2 !text-xs" placeholder="Texte" value={s.text} onChange={(e) => setForm({ ...form, sections: form.sections.map((x, j) => j === i ? { ...x, text: e.target.value } : x) })} data-testid={`gallery-section-text-${i}`} />
                  <div className="flex gap-2">
                    <input className="input-ae !py-2 !text-xs" placeholder="URL photo (Médiathèque)" value={s.image} onChange={(e) => setForm({ ...form, sections: form.sections.map((x, j) => j === i ? { ...x, image: e.target.value } : x) })} data-testid={`gallery-section-image-${i}`} />
                  </div>
                </div>
              ))}
              {form.sections.length === 0 && <p className="mt-2 text-[11px] text-muted-foreground">Sans section : la galerie photo classique s'affiche.</p>}
            </div>

            {/* L'avis des mariés */}
            <div className="rounded-lg border border-gold/40 bg-gold-light/50 p-4" data-testid="gallery-review-editor">
              <p className="label-ae !mb-2">L'avis des mariés (facultatif)</p>
              <textarea className="input-ae min-h-16 !text-xs" placeholder="Témoignage réel du couple…" value={form.review_text} onChange={(e) => setForm({ ...form, review_text: e.target.value })} data-testid="gallery-review-text" />
              <div className="mt-2 grid gap-2 sm:grid-cols-3">
                <input className="input-ae !py-2 !text-xs" placeholder="Auteurs (ex : Julie & Clément)" value={form.review_author} onChange={(e) => setForm({ ...form, review_author: e.target.value })} data-testid="gallery-review-author" />
                <select className="input-ae !py-2 !text-xs" value={form.review_source} onChange={(e) => setForm({ ...form, review_source: e.target.value })} data-testid="gallery-review-source">
                  <option value="">Source…</option>
                  <option value="Google">Google</option>
                  <option value="Mariages.net">Mariages.net</option>
                  <option value="Direct">Recueilli directement</option>
                </select>
                <select className="input-ae !py-2 !text-xs" value={form.review_rating} onChange={(e) => setForm({ ...form, review_rating: Number(e.target.value) })} data-testid="gallery-review-rating">
                  {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} étoile{n > 1 ? "s" : ""}</option>)}
                </select>
              </div>
            </div>

            {/* Prestataires du jour */}
            <div className="rounded-lg border border-border bg-white p-4" data-testid="gallery-vendors-editor">
              <div className="flex items-center justify-between">
                <p className="label-ae !mb-0">Prestataires du jour</p>
                <button type="button" onClick={() => setForm({ ...form, vendors: [...form.vendors, { name: "", role: "", link: "" }] })} className="btn-secondary-ae !px-3.5 !py-1.5 !text-[10px]" data-testid="gallery-add-vendor">
                  <Plus className="h-3.5 w-3.5" /> Ajouter
                </button>
              </div>
              {form.vendors.map((v, i) => (
                <div key={i} className="mt-2 flex gap-2" data-testid={`gallery-vendor-${i}`}>
                  <input className="input-ae !py-2 !text-xs" placeholder="Nom" value={v.name} onChange={(e) => setForm({ ...form, vendors: form.vendors.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })} data-testid={`gallery-vendor-name-${i}`} />
                  <input className="input-ae !py-2 !text-xs" placeholder="Métier (ex : Photographe)" value={v.role} onChange={(e) => setForm({ ...form, vendors: form.vendors.map((x, j) => j === i ? { ...x, role: e.target.value } : x) })} data-testid={`gallery-vendor-role-${i}`} />
                  <input className="input-ae !py-2 !text-xs" placeholder="Lien (facultatif)" value={v.link} onChange={(e) => setForm({ ...form, vendors: form.vendors.map((x, j) => j === i ? { ...x, link: e.target.value } : x) })} data-testid={`gallery-vendor-link-${i}`} />
                </div>
              ))}
            </div>

            {/* Matériel associé */}
            <div>
              <label className="label-ae" htmlFor="g-products">Matériel loué — « Louer la décoration de ce mariage » (slugs, virgules)</label>
              <input id="g-products" className="input-ae" placeholder="ex : arche-triangulaire-bois, housses-blanches" value={form.product_slugs} onChange={(e) => setForm({ ...form, product_slugs: e.target.value })} data-testid="gallery-products" />
            </div>

            <button type="button" onClick={submit} disabled={form.title.length < 2} className="btn-primary-ae modal-sticky-btn w-full disabled:opacity-50" data-testid="gallery-submit">
              {editing ? "Enregistrer" : "Créer (brouillon)"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <MediaPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} multiple onSelect={(imgs) => setForm({ ...form, images: [...(form.images.trim() ? form.images.trim().split("\n").filter(Boolean) : []), ...imgs.map((m) => m.url)].join("\n") })} title="Ajouter des photos à la galerie" />
    </div>
  );
}
